/**
 * Benchmark price history — the event-time prices the DCA comparison needs.
 *
 * WHY THIS EXISTS
 * calcBenchmarkDCA answers "what if I had just bought VOO on the same schedule". To do
 * that honestly it needs the price AT each contribution month. The only source it had was
 * S.snapshots, which is capped at 365 days and only written on days the dashboard
 * rendered — so most historical months had no price. The original code substituted
 * TODAY's price, which buys and values at the same number and therefore scores exactly 0%
 * for that month, dragging the benchmark toward zero and fabricating alpha. That was
 * fixed by EXCLUDING unpriced months; this module fills them in properly. (audit 2026-08-01)
 *
 * SIZE. One float per ticker per month. Twenty-four months of VOO is roughly 600 bytes —
 * two orders of magnitude below S.signals — so this respects the no-Supabase-bloat rule.
 * Months are stored once and never refetched: a historical monthly close does not change.
 *
 * TRIGGERED BY THE USER, NEVER BY A RENDER. A render path that fetches and writes state
 * is how surprise network calls and surprise cloud writes happen. The Dashboard shows a
 * button when coverage is incomplete; nothing here runs on its own.
 */

/** Monthly closes already known for a ticker: { 'YYYY-MM': price }. Never throws. */
export function knownMonths(S, ticker = 'VOO') {
  const m = S && S.benchmarkPrices && S.benchmarkPrices[ticker];
  return (m && typeof m === 'object') ? m : {};
}

/**
 * Price for a contribution month, event-time first.
 * Backfilled monthly close -> that month's snapshot -> null. Never falls back to a
 * current price: that is the defect this module exists to remove.
 */
export function benchmarkPriceFor(S, ticker, month) {
  const stored = knownMonths(S, ticker)[month];
  if (stored > 0) return stored;
  const snap = (S && S.snapshots || []).find(s =>
    s && typeof s.date === 'string' && s.date.startsWith(month) &&
    s.benchmarks && s.benchmarks[ticker] > 0);
  const v = snap && snap.benchmarks[ticker];
  return v > 0 ? v : null;
}

/** Months that still have no price from either source. */
export function missingMonths(S, months, ticker = 'VOO') {
  return (months || []).filter(m => benchmarkPriceFor(S, ticker, m) == null);
}

/**
 * Fetch monthly closes and merge them in. Returns a report rather than a boolean so the
 * caller can tell "nothing was missing" from "the fetch failed" — those must never look
 * the same.
 *
 * @returns {Promise<{ok:boolean, added:number, stillMissing:string[], error?:string}>}
 */
export async function backfillBenchmarkPrices(S, months, ticker = 'VOO', fetchImpl) {
  const want = missingMonths(S, months, ticker);
  if (!want.length) return { ok: true, added: 0, stillMissing: [] };

  const doFetch = fetchImpl || ((url) => fetch(url).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }));

  let payload;
  try {
    payload = await doFetch(`/api/ta?mode=monthly&tickers=${encodeURIComponent(ticker)}&range=10y`);
  } catch (e) {
    return { ok: false, added: 0, stillMissing: want, error: (e && e.message) || 'fetch failed' };
  }

  const series = payload && payload.monthly && payload.monthly[ticker];
  if (!series || typeof series !== 'object') {
    return { ok: false, added: 0, stillMissing: want, error: 'no monthly series returned' };
  }

  if (!S.benchmarkPrices) S.benchmarkPrices = {};
  if (!S.benchmarkPrices[ticker]) S.benchmarkPrices[ticker] = {};
  const store = S.benchmarkPrices[ticker];
  let added = 0;
  for (const m of want) {
    const px = Number(series[m]);
    // Only write a month we actually asked for and actually received. Writing the whole
    // series would grow the blob with months that carry no contribution.
    if (px > 0 && !(store[m] > 0)) { store[m] = +px.toFixed(4); added++; }
  }
  return { ok: true, added, stillMissing: missingMonths(S, months, ticker) };
}
